import "./PasteOptionsWidget.scss";

import { useTranslations } from "providers";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

export interface PasteOptionsWidgetProps {
  anchorRect: DOMRect;
  onKeepFormatting: () => void;
  /** Present only when there is a plain-text alternative to switch to */
  onPlainText?: () => void;
  onCancel: () => void;
}

export const PasteOptionsWidget: React.FC<PasteOptionsWidgetProps> = ({
  anchorRect,
  onKeepFormatting,
  onPlainText,
  onCancel,
}) => {
  const i18n = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  const expand = useCallback(() => setExpanded(true), []);

  // Expand on Ctrl key, dismiss on other keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Control") {
        setExpanded(true);
        return;
      }
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      // Any other key dismisses widget (user started typing — keep the paste)
      onKeepFormatting();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [onKeepFormatting, onCancel]);

  // Dismiss on click outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        onKeepFormatting();
      }
    };

    window.addEventListener("mousedown", handleMouseDown, true);
    return () => window.removeEventListener("mousedown", handleMouseDown, true);
  }, [onKeepFormatting]);

  // anchorRect is in viewport coordinates (from getBoundingClientRect),
  // and the widget uses position:fixed — do NOT add window.scroll offsets.
  const style: React.CSSProperties = {
    top: anchorRect.bottom + 2,
    left: anchorRect.left + 4,
  };

  return ReactDOM.createPortal(
    <div className="paste-options-widget" style={style} ref={widgetRef}>
      <button
        className="paste-options-widget__trigger"
        type="button"
        title={i18n.RichText_Paste_Options_Trigger_Tooltip}
        onMouseEnter={expand}
        onClick={expand}
        tabIndex={-1}
      >
        <i className="ms-Icon ms-Icon--Paste" />
        <span className="paste-options-widget__caret">▾</span>
      </button>

      {expanded && (
        <div
          className="paste-options-widget__menu"
          role="toolbar"
          aria-label={i18n.RichText_Paste_Options_Trigger_Tooltip}
        >
          <button
            className="paste-options-widget__action paste-options-widget__action--active"
            type="button"
            title={i18n.RichText_Paste_KeepFormatting_Tooltip}
            onClick={onKeepFormatting}
            tabIndex={0}
            autoFocus
          >
            <i className="ms-Icon ms-Icon--Paste" />
          </button>

          <div className="paste-options-widget__separator" />

          {onPlainText && (
            <>
              <button
                className="paste-options-widget__action"
                type="button"
                title={i18n.RichText_Paste_PlainText_Tooltip}
                onClick={onPlainText}
                tabIndex={0}
              >
                <i className="ms-Icon ms-Icon--PasteAsText" />
              </button>

              <div className="paste-options-widget__separator" />
            </>
          )}

          <button
            className="paste-options-widget__action"
            type="button"
            title={i18n.RichText_Paste_Cancel_Tooltip}
            onClick={onCancel}
            tabIndex={0}
          >
            <i className="ms-Icon ms-Icon--Cancel" />
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};
